import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, User, Shield, ShoppingBag, Pencil, Trash2, UserCheck, UserX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tinkah_token")}` }
});

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", pin: "", role: "sales" });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!editing && (!form.pin || form.pin.length < 4)) {
      toast.error("PIN must be at least 4 digits");
      return;
    }
    try {
      if (editing) {
        const updateData = { name: form.name, email: form.email };
        if (form.pin) updateData.pin = form.pin;
        await axios.put(`${API}/users/${editing.id}`, updateData, getAuthHeaders());
        toast.success("User updated");
      } else {
        await axios.post(`${API}/users`, form, getAuthHeaders());
        toast.success("User created");
      }
      setDialogOpen(false);
      setForm({ name: "", email: "", pin: "", role: "sales" });
      setEditing(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save user");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: !user.is_active }, getAuthHeaders());
      toast.success(user.is_active ? "User deactivated" : "User activated");
      loadUsers();
    } catch (err) {
      toast.error("Failed to update user");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${user.id}`, getAuthHeaders());
      toast.success("User deleted");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ name: user.name, email: user.email, pin: "", role: user.role });
    setDialogOpen(true);
  };

  const currentUser = JSON.parse(localStorage.getItem("tinkah_user") || "{}");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-['Space_Mono'] text-3xl tracking-tight uppercase text-[#5C4D24]">Users</h2>
          <p className="text-sm text-[#8B7536] mt-1">Manage team access & roles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditing(null); setForm({ name: "", email: "", pin: "", role: "sales" }); } }}>
          <DialogTrigger asChild>
            <button
              data-testid="add-user-btn"
              className="flex items-center gap-2 px-6 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95"
            >
              <Plus className="w-4 h-4" /> Add User
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md rounded-none border-[#D6D6CF]">
            <DialogHeader>
              <DialogTitle className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">
                {editing ? "Edit User" : "New User"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">
                  PIN {editing ? "(leave blank to keep current)" : "*"}
                </Label>
                <Input
                  type="password"
                  maxLength={6}
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
                  className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']"
                  placeholder="4-6 digits"
                  data-testid="user-pin-input"
                />
              </div>
              {!editing && (
                <div>
                  <Label className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536]">Role *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1 rounded-none border-[#D6D6CF] font-['Space_Mono']" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="px-4 py-2 border border-[#D6D6CF] text-[#5C4D24] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#F5F5F0] transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-user-btn"
                  className="px-4 py-2 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all"
                >
                  {editing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {users.map((user) => (
          <div
            key={user.id}
            className={`bg-white border p-5 flex items-center justify-between ${
              user.is_active ? "border-[#D6D6CF]" : "border-[#D6D6CF] opacity-60"
            }`}
            data-testid={`user-card-${user.id}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center ${
                user.role === "super_admin" ? "bg-[#9F7D23]/20" : "bg-[#F5F5F0]"
              }`}>
                {user.role === "super_admin" ? (
                  <Shield className="w-6 h-6 text-[#9F7D23]" />
                ) : (
                  <ShoppingBag className="w-6 h-6 text-[#8B7536]" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-['Space_Mono'] text-sm text-[#5C4D24]">{user.name}</p>
                  {!user.is_active && (
                    <span className="px-2 py-0.5 bg-[#9F3A3A]/10 text-[#9F3A3A] text-[10px] font-['Space_Mono'] uppercase">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#8B7536]">{user.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-['Space_Mono'] tracking-widest uppercase ${
                  user.role === "super_admin" 
                    ? "bg-[#9F7D23]/10 text-[#9F7D23]" 
                    : "bg-[#F5F5F0] text-[#8B7536]"
                }`}>
                  {user.role === "super_admin" ? "Super Admin" : "Sales"}
                </span>
              </div>
            </div>
            
            {user.id !== currentUser.id && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleActive(user)}
                  className={`p-2 transition-colors ${
                    user.is_active 
                      ? "text-[#9F3A3A] hover:bg-[#9F3A3A]/10" 
                      : "text-[#4B6F44] hover:bg-[#4B6F44]/10"
                  }`}
                  title={user.is_active ? "Deactivate" : "Activate"}
                  data-testid={`toggle-user-${user.id}`}
                >
                  {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => openEdit(user)}
                  className="p-2 text-[#8B7536] hover:bg-[#F5F5F0] transition-colors"
                  data-testid={`edit-user-${user.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(user)}
                  className="p-2 text-[#9F3A3A] hover:bg-[#9F3A3A]/10 transition-colors"
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
